import { api } from "@api";
import { authorizationSignal, getUser } from "@api/authorization";
import { links } from "@links";
import { For, Show, createSignal, onCleanup, onMount } from "solid-js";
import { HamburgerIcon } from "./icons/HamburgerIcon";

const LINK_STYLES =
  "block py-4 pr-4 pl-3 rounded hover:text-light-primary lg:bg-transparent lg:p-0";

export const UserNav = () => {
  const [menuOpen, setMenuOpen] = createSignal(false);
  const [bgColor, setBgColor] = createSignal("bg-transparent");
  const [activeLink, setActiveLink] = createSignal("");
  const [auth] = authorizationSignal;

  const isProd =
    import.meta.env.PUBLIC_SITE_URL === "https://riddlerroadrally.com";

  const handleScroll = () => {
    if (window.scrollY === 0) {
      setBgColor("bg-transparent");
    } else {
      setBgColor("bg-primary");
    }
  };

  const loggedIn = () => auth() && getUser();
  const isAdmin = () =>
    auth() && ["manager", "admin"].includes(getUser()?.userType ?? "");

  onMount(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", handleScroll);
      console.log(window.location.pathname);
      setActiveLink(window.location.pathname);
    }
  });

  onCleanup(() => {
    if (typeof window !== "undefined")
      window.removeEventListener("scroll", handleScroll);
  });

  const availableLinks = () => {
    const signedIn = loggedIn();
    return [
      {
        label: "Home",
        href: links.home,
        public: true,
      },
      {
        label: "Events",
        children: [
          {
            label: "Live Events",
            href: links.events,
          },
          {
            label: "Rally On Demand",
            href: links.pastEvents,
            public: true,
          },
          {
            label: "Private Events",
            href: links.privateEvents,
            public: true,
          },
        ],
        public: true,
      },
      {
        label: "Results",
        href: links.results,
        public: true,
      },
      {
        label: "How To Play",
        href: links.howToPlay,
        public: true,
      },
      {
        label: "Swag",
        href: links.swag,
        public: true,
      },
      {
        label: "News",
        href: links.news,
        public: true,
      },
      {
        label: "My Teams",
        href: links.teams,
        public: false,
      },
      {
        label: "Profile",
        href: links.profile,
        public: false,
      },
    ].filter((link) => link.public || signedIn);
  };

  return (
    <>
      {isProd ? (
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-TLS5HRRK"
            height="0"
            width="0"
            style="display:none;visibility:hidden"
          ></iframe>
        </noscript>
      ) : (
        <></>
      )}
      <nav
        class={`border-gray-200 fixed w-full z-40 ${
          menuOpen() ? "h-screen" : "h-20"
        }`}
      >
        <div
          class={`${menuOpen() ? "bg-primary" : bgColor()} ${
            menuOpen() ? "duration-0" : "duration-500"
          } flex flex-wrap py-3 px-8 justify-between items-center w-full transition  ease-in-out`}
        >
          <a href="/" class="flex items-center">
            <img
              src="/img/logo.png"
              class="h-14"
              alt="Riddler Road Rally Logo"
            />
          </a>
          <button
            data-collapse-toggle="navbar-default"
            type="button"
            onClick={() => {
              setMenuOpen((e) => !e);
            }}
            class="inline-flex items-center p-2 ml-3 text-sm text-gray-500 rounded-lg lg:hidden focus:outline-none"
            aria-controls="navbar-default"
            aria-expanded="false"
          >
            <span class="sr-only">Open main menu</span>
            <HamburgerIcon />
          </button>
          <div
            class={`w-full lg:block lg:w-auto`}
            classList={{
              "max-h-[100vh]": menuOpen(),
              hidden: !menuOpen(),
            }}
            id="navbar-default"
          >
            <ul
              class={`flex items-center flex-col p-4 mt-2 rounded-lg text-text-white lg:flex-row lg:space-x-8 lg:mt-0 lg:text-sm lg:font-medium lg:border-0 ${
                menuOpen() ? "mb-36" : ""
              }`}
            >
              <For each={availableLinks()}>
                {(link) => {
                  const [open, setOpen] = createSignal(false);
                  return (
                    <li>
                      <Show
                        when={link.href}
                        fallback=<details
                          class="dropdown select-none"
                          open={open()}
                          onToggle={(e) => {
                            setOpen(e.currentTarget.open);
                          }}
                        >
                          <summary
                            class="cursor-pointer flex justify-between items-center p-1 gap-2 rounded-t hover:text-light-primary"
                            classList={{
                              "text-light-primary bg-white": open(),
                            }}
                          >
                            {link.label}
                            <svg
                              class="fill-current w-4 h-4"
                              classList={{
                                "-rotate-90": open(),
                                "rotate-90": !open(),
                              }}
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                            >
                              <path d="M12.95 10.707l.707-.707L8 4.343 6.586 5.757 10.828 10l-4.242 4.243L8 15.657l4.95-4.95z" />
                            </svg>
                          </summary>
                          <ul class="dropdown-content shadow menu z-[10000] rounded-b rounded-r bg-base-100 w-40">
                            <For each={link.children}>
                              {(child) => (
                                <li>
                                  <a
                                    href={child.href}
                                    class={
                                      "hover:text-light-primary text-primary p-1 rounded"
                                    }
                                    classList={{
                                      "text-light-primary":
                                        activeLink().replace("/", "") ===
                                        child.href
                                          .toLowerCase()
                                          .replace("/", ""),
                                    }}
                                  >
                                    {child.label}
                                  </a>
                                </li>
                              )}
                            </For>
                          </ul>
                        </details>
                      >
                        <a
                          href={link.href}
                          class={LINK_STYLES}
                          classList={{
                            "text-light-primary":
                              activeLink().replace("/", "") ===
                              link.href!.toLowerCase().replace("/", ""),
                          }}
                        >
                          {link.label}
                        </a>
                      </Show>
                    </li>
                  );
                }}
              </For>
              <Show when={isAdmin()}>
                <li>
                  <a
                    href={links.adminHome}
                    class={LINK_STYLES}
                    classList={{
                      "text-light-primary": activeLink() === links.adminHome,
                    }}
                  >
                    Admin
                  </a>
                </li>
              </Show>
              <Show
                when={!loggedIn()}
                fallback={
                  <li>
                    <button
                      class={`btn btn-primary btn-sm ${
                        menuOpen() ? "mb-20" : ""
                      }`}
                      onClick={() => {
                        api.logout();
                        window.location.href = links.home;
                      }}
                    >
                      Sign Out
                    </button>
                  </li>
                }
              >
                <li>
                  <a href={links.login} class={LINK_STYLES}>
                    Sign In
                  </a>
                </li>
                <li>
                  <button
                    class="btn btn-primary btn-sm"
                    onClick={() => {
                      window.location.href = links.createAccount;
                    }}
                  >
                    Create Account
                  </button>
                </li>
              </Show>
            </ul>
          </div>
        </div>
      </nav>
    </>
  );
};

export default UserNav;
